import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  setLoginSuccess,
  setUnitPreference,
} from '../../../reducers/userDetailsSlice';
import { useAppSelector } from '../../../reducers/rootReducer';
import { selectInterceptorsStatus } from '../../../reducers/interceptorsSlice';
import { getVectorConfiguration } from '../../../reducers/vectorConfigurationSlice';
import { getUserDetails } from '../../../common/components/UserDetails/actions';
import {
  getLanguages,
  translationSelectors,
} from '../../../reducers/translationSlice';
import i18n, { updateI18nLanguage } from '../../../common/i18n/i18n';
import { useLelyAuth } from '../../../auth/useLelyAuth';
import {
  DEFAULT_UNIT,
  PREFERRED_UNIT,
  UNIT_IMPERIAL,
  UNIT_METRIC,
} from '@/common/constants';

export default function useAuth0PostLoginActions() {
  const dispatch = useDispatch();
  const { isAuthenticated, user, isLoading: auth0Loading } = useLelyAuth();
  const isLoggedIn = useAppSelector((state) => state.userDetails.isLoggedIn);
  const interceptorsStatus = useAppSelector(selectInterceptorsStatus);
  const translations = useAppSelector(translationSelectors.selectTranslations);
  const preferredLanguage = useAppSelector(
    translationSelectors.selectPreferredLanguageCode,
  );
  useEffect(() => {
    const handleAuthStateChange = async () => {
      const isLogoutPage = window.location.pathname === '/logout';
      if (
        isAuthenticated &&
        user?.allowltda &&
        !isLogoutPage &&
        !isLoggedIn &&
        interceptorsStatus === 'initialized'
      ) {
        dispatch(setLoginSuccess());
      }
    };

    handleAuthStateChange();
  }, [isAuthenticated, user, dispatch, interceptorsStatus]);

  useEffect(() => {
    if (
      isAuthenticated &&
      user?.allowltda &&
      !auth0Loading &&
      interceptorsStatus === 'initialized'
    ) {
      // User is already authenticated, ensure translations are loaded
      dispatch(getUserDetails());
      dispatch(getLanguages());
      dispatch(getVectorConfiguration());
    }
  }, [isAuthenticated, auth0Loading, interceptorsStatus, dispatch]);

  useEffect(() => {
    if (!isEmpty(translations)) {
      // Get all namespaces from the response
      const namespaces = Object.keys(translations);
      // Add each namespace bundle
      namespaces.forEach((namespace) => {
        i18n.addResourceBundle(
          preferredLanguage, // language
          namespace, // namespace
          translations[namespace], // data
          true, // deep merge
          true, // overwrite
        );
      });
      updateI18nLanguage(preferredLanguage);
    }
  }, []);

  // Moved here from old user dialog
  useEffect(() => {
    if (interceptorsStatus === 'initialized' && isAuthenticated) {
      const storedUnit = localStorage.getObject(PREFERRED_UNIT) as
        | string
        | null;
      const unitPreference =
        storedUnit === UNIT_METRIC || storedUnit === UNIT_IMPERIAL
          ? storedUnit
          : DEFAULT_UNIT;

      if (!storedUnit) {
        localStorage.setObject(PREFERRED_UNIT, unitPreference as any);
      }

      dispatch(setUnitPreference(unitPreference));
    }
  }, [interceptorsStatus, isAuthenticated]);
}
